// deploy-pipelines-run-logs (Wave 3) AC-12 — the reason -> copy map for every state in which
// the Build stage has NO log text to show. Extracted as a pure function, in its own file, so
// the copy for each state is asserted directly by a pure-logic jest suite rather than only
// through the component.
//
// Returns `null` when there IS renderable log text — that is the caller's signal to render the
// log body instead of a message. It never returns an empty string, so `message ?? render the
// log` can never produce a blank bordered box (AC-12).
//
// The states come from spec OQ-4. Note what is NOT here: no "error" copy. A run that never
// built, a build record that has been removed, and a build that printed nothing are all
// ordinary outcomes, and calling any of them an error would be a lie. A genuine FETCH failure
// is a different thing entirely and is rendered by the panel as its own non-blocking notice
// (AC-13), on top of whatever was last fetched.

// The closed set the BE emits (BE: DTOs/PipelineRunDto.cs, PipelineRunLogDto.ReasonNotBuilt /
// ReasonSourceUnavailable). Closed on the wire is not closed in practice — a BE that adds a
// third reason ships independently of this bundle — hence GENERIC_UNAVAILABLE_MESSAGE below.
export const RunLogReason = {
  NotBuilt: 'NotBuilt',
  SourceUnavailable: 'SourceUnavailable',
};

export const RUN_LOG_REASON_MESSAGE = {
  [RunLogReason.NotBuilt]: 'No build log — this run never started a build.',
  [RunLogReason.SourceUnavailable]: 'The build record for this run is no longer available.',
};

// AC-12's explicit requirement: an UNRECOGNISED `Reason` must fall back to a defined message,
// never render nothing. This is the fallback. It is deliberately vaguer than the two mapped
// reasons — it is what we can honestly say when we do not recognise what the server told us.
export const GENERIC_UNAVAILABLE_MESSAGE = 'No build log is available for this run.';

// The source exists but has written nothing yet. Two different facts depending on whether the
// build is still running, and the difference matters: "not yet" invites the reader to wait,
// "produced no output" tells them waiting is pointless.
export const EMPTY_LOG_BUILDING_MESSAGE = 'The build has not written any output yet.';
export const EMPTY_LOG_TERMINAL_MESSAGE = 'The build produced no output.';

// Independent review of the M1 fix. For a CANCELLED run, `EMPTY_LOG_TERMINAL_MESSAGE` is an
// affirmative falsehood far more often than not: `DockerImageMetaData.Logs` is written only by
// `DockerRepositoryHostedService`, whose interval defaults to 120s, so a build cancelled at 0:40
// has emitted plenty of output and stored none of it yet. Saying "produced no output" states as
// fact the one thing we cannot know at that moment. This wording claims only what is true — we do
// not have the output — and names the reason it may appear later, which is also why the panel
// stays worth opening.
export const EMPTY_LOG_CANCELLED_MESSAGE =
  'No build output was captured before the run was cancelled. If the build had already written output, it can take up to a couple of minutes to appear here.';

/**
 * @param {object|null|undefined} log  PipelineRunLogDto as received, or null before the first
 *                                     response has landed.
 * @param {boolean} isBuilding         Whether the run's Status is currently Building.
 * @param {boolean} [isCancelled]      Whether the run's Status is Cancelled. Optional so every
 *                                     pre-existing two-argument call site keeps its exact
 *                                     behaviour (`undefined` is falsy) — this only ever adds a
 *                                     third empty-state branch, it never changes the other two.
 * @returns {string|null}              The copy to render, or null when `log.Logs` should be
 *                                     rendered instead.
 */
export const resolveRunLogMessage = (log, isBuilding, isCancelled) => {
  // Nothing fetched yet. The panel owns the loading affordance; there is no honest sentence to
  // put here, and inventing one would be indistinguishable from a resolved empty state.
  if (!log) {
    return null;
  }

  if (!log.Available) {
    // An OWN-property check, not `RUN_LOG_REASON_MESSAGE[log.Reason] ?? fallback`. The naive
    // form is wrong in a way that is easy to miss and impossible to see in review: a Reason of
    // 'toString' (or 'constructor', or 'valueOf') resolves up the prototype chain to a
    // FUNCTION, which is not nullish, so `??` never fires and the panel would try to render a
    // function as its copy. Caught by the unrecognised-reason case in the sibling test.
    return Object.prototype.hasOwnProperty.call(RUN_LOG_REASON_MESSAGE, log.Reason)
      ? RUN_LOG_REASON_MESSAGE[log.Reason]
      : GENERIC_UNAVAILABLE_MESSAGE;
  }

  // Available, but nothing in it. `Logs` is '' per the contract; the null/undefined guards are
  // belt-and-braces against a shape we did not specify rather than a case the BE emits.
  if (!log.Logs) {
    if (isBuilding) {
      return EMPTY_LOG_BUILDING_MESSAGE;
    }
    // Checked after `isBuilding` so a cancel racing the poll cannot produce the cancelled copy for
    // a run the FE still believes is building — the two are mutually exclusive on the wire anyway.
    return isCancelled ? EMPTY_LOG_CANCELLED_MESSAGE : EMPTY_LOG_TERMINAL_MESSAGE;
  }

  return null;
};

export default resolveRunLogMessage;
